import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { type TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';

import { analyticsListenerMiddleware } from './middlewares/analyticsMiddleware';
import logger from './middlewares/loggerMiddleware';
import { loginToastListenerMiddleware } from './middlewares/loginToastMiddleware';
import externalAuthSlice from './slices/externalAuthSlice';
import footballSlice from './slices/football/footballSlice';
import modalSlice from './slices/modalSlice';
import originalsTransition from './slices/originalsTransition';
import passInfoSlice from './slices/passInfoSlice';
import paymentSlice from './slices/paymentSlice';
import temporarySlice from './slices/temporarySlice';
import toastSlice from './slices/toastSlice';
import userSlice from './slices/userSlice';

const rootReducer = combineReducers({
  user: userSlice.reducer,
  passInfo: passInfoSlice.reducer,
  temporary: temporarySlice.reducer,
  modal: modalSlice.reducer,
  toast: toastSlice.reducer,
  externalAuth: externalAuthSlice.reducer,
  payment: paymentSlice.reducer,
  footballCommon: footballSlice.reducer,
  originalsTransition: originalsTransition.reducer,
});

export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    process.env.NODE_ENV === 'production'
      ? getDefaultMiddleware().concat([loginToastListenerMiddleware.middleware, analyticsListenerMiddleware.middleware])
      : getDefaultMiddleware({
          serializableCheck: false,
        }).concat([logger, loginToastListenerMiddleware.middleware, analyticsListenerMiddleware.middleware]),
  devTools: process.env.NEXT_PUBLIC_MODE !== 'production',
  enhancers: (getDefaultEnhancers) => getDefaultEnhancers(),
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
export const useAppDispatch = () => useDispatch<AppDispatch>();
export default store;
